import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGeoLocationsFormContext } from "shared/components/organization/geolocations/context";
import { GeoFilter, GeoLayer } from "shared/components/organization/geolocations/types";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { catching } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { geozod } from "shared/utils/zod";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

export const GEO_QUERY_KEY = ["map", "customer", "geofilters"];

export function useGeoLocations() {
    const { data: layers } = useSuspenseQuery(queries.layers);
    const form = useGeoLocationsFormContext();
    const createShape = useGraphqlMutation(muts.createShape);
    const updateShape = useGraphqlMutation(muts.updateShape);
    const archiveShape = useGraphqlMutation(muts.archiveShape);

    function setDrawingState(state: { layerId: string; locked: boolean; shape: GeoFilter | null }) {
        form.setFieldValue("drawingLayerId", state.layerId);
        form.setFieldValue("drawingLayerLocked", state.locked);
        const polygon =
            maybe(catching(() => state.shape?.boundary && JSON.parse(state.shape.boundary)))?.take(
                (obj) => geozod.polygon().safeParse(obj).data,
            ) ?? null;
        form.setFieldValue("polygon", polygon);
        form.setFieldValue("editingShape", state.shape);
    }

    async function handleFetchLayerShapes(layerId: string) {
        const layers = queryClient.getQueryData<GeoLayer[]>(GEO_QUERY_KEY);
        const layer = layers?.find((l) => l.id === layerId);

        if (!!layer?.filters) return;

        await queryClient
            .fetchQuery({
                queryKey: [...GEO_QUERY_KEY, "layer", layerId, "shapes"],
                queryFn: () => fetchPaginated((offset) => fetchShapesPage(layerId, offset)),
            })
            .then((shapes) => {
                queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
                    old ? old.map((l) => (l.id === layerId ? { ...l, filters: shapes } : l)) : old,
                );
            })
            .catch(notify.catch);
    }

    useEffect(
        () =>
            layers.forEach((layer) => {
                if (form.values.hiddenLayerIds[layer.id] === false && !layer.filters) {
                    void handleFetchLayerShapes(layer.id);
                }
            }),
        [layers, form.values.hiddenLayerIds],
    );

    async function handleArchiveShape(id: string) {
        const modal = await notify.confirm("Are you sure you want to archive this shape?");
        await archiveShape
            .mutateAsync({ id })
            .then(() => {
                notify.show.success("Shape archived");
                queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
                    old
                        ? old.map((layer) => ({
                              ...layer,
                              filters: layer.filters?.filter((filter) => filter.id !== id),
                          }))
                        : old,
                );
            })
            .catch(notify.catch);
        modal.close();
    }

    function updateShapeInLayer(layer: GeoLayer, shapeId: string, updatedShape: GeoFilter): GeoLayer {
        return !layer.filters
            ? layer
            : {
                  ...layer,
                  filters: layer.filters.some((f) => f.id === shapeId)
                      ? layer.filters.map((f) => (f.id === shapeId ? updatedShape : f))
                      : [...layer.filters, updatedShape],
              };
    }

    function removeShapeFromLayer(layer: GeoLayer, shapeId: string): GeoLayer {
        return !layer.filters
            ? layer
            : {
                  ...layer,
                  filters: layer.filters.filter((f) => f.id !== shapeId),
              };
    }

    function updateQueryLayerData(
        editingShape: GeoFilter,
        name: string,
        polygon: GeoJSON.Polygon,
        destinationLayerId: string,
        originalLayerId: string | undefined,
    ) {
        queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
            old
                ? old.map((layer) => {
                      if (layer.id === destinationLayerId) {
                          const updatedShape = { ...editingShape, name, boundary: JSON.stringify(polygon) };
                          return updateShapeInLayer(layer, editingShape.id, updatedShape);
                      }

                      if (layer.id === originalLayerId && originalLayerId !== destinationLayerId) {
                          return removeShapeFromLayer(layer, editingShape.id);
                      }

                      return layer;
                  })
                : old,
        );
    }

    function updateCacheAfterShapeCreate(layerId: string, shapeId: string, name: string, polygon: GeoJSON.Polygon) {
        queryClient.setQueryData<GeoLayer[]>(GEO_QUERY_KEY, (old) =>
            old
                ? old.map((layer) => {
                      if (layer.id !== layerId) return layer;
                      if (!layer.filters) return layer;

                      const newShape: GeoFilter = {
                          id: shapeId,
                          name,
                          boundary: JSON.stringify(polygon),
                      };

                      return {
                          ...layer,
                          filters: [...layer.filters, newShape],
                      };
                  })
                : old,
        );
    }

    function handleSaveShape(name: string, polygon: GeoJSON.Polygon) {
        const { drawingLayerId, editingShape } = form.values;
        if (!drawingLayerId || !polygon) return;

        if (editingShape) {
            const originalLayer = layers.find((l) => l.filters?.some((f) => f.id === editingShape.id));
            const originalLayerId = originalLayer?.id;

            void updateShape
                .mutateAsync({
                    id: editingShape.id,
                    name,
                    boundary: JSON.stringify(polygon),
                    layerId: drawingLayerId,
                })
                .then(() => {
                    notify.show.success("Shape updated");
                    updateQueryLayerData(editingShape, name, polygon, drawingLayerId, originalLayerId);
                    resetDrawingState();
                })
                .catch(notify.catch);
            return;
        }

        void createShape
            .mutateAsync({ name, boundary: JSON.stringify(polygon), layerId: drawingLayerId })
            .then((result) => {
                notify.show.success("Shape created");
                updateCacheAfterShapeCreate(drawingLayerId, result.createGeoFilter.id, name, polygon);
                resetDrawingState();
            })
            .catch(notify.catch);
    }

    function resetDrawingState() {
        form.setFieldValue("drawingLayerId", null);
        form.setFieldValue("drawingLayerLocked", false);
        form.setFieldValue("polygon", null);
        form.setFieldValue("editingShape", null);
    }

    return {
        layers,
        activeLayerId: form.values.activeLayerId,
        drawingLayerId: form.values.drawingLayerId,
        drawingLayerLocked: form.values.drawingLayerLocked,
        polygon: form.values.polygon,
        editingShape: form.values.editingShape,
        hiddenLayerIds: form.values.hiddenLayerIds,
        hoveredShapeId: form.values.hoveredShapeId,
        isSaving: createShape.isPending || updateShape.isPending,
        setPolygon: (polygon: GeoJSON.Polygon | null) => form.setFieldValue("polygon", polygon),
        setHoveredShapeId: (id: string | null) => form.setFieldValue("hoveredShapeId", id),
        setActiveLayerId: (id: string | null) => form.setFieldValue("activeLayerId", id),
        setDrawingLayerId: (id: string | null) => form.setFieldValue("drawingLayerId", id),
        resetDrawingState,
        startNewShape: (layerId: string, locked = true) => setDrawingState({ layerId, locked, shape: null }),
        handleArchiveShape,
        handleEditShape: (shape: GeoFilter, layerId: string) => setDrawingState({ layerId, locked: true, shape }),
        handleSaveShape,
        handleToggleLayerVisibility: (layerId: string) => {
            const current = form.values.hiddenLayerIds[layerId];
            form.setFieldValue(`hiddenLayerIds.${layerId}`, current === false ? true : false);
        },
        handleGlobalAddShape: () => {
            const { drawingLayerId } = form.values;
            if (drawingLayerId) return;
            const targetLayerId = layers[0].id;
            form.setFieldValue("activeLayerId", targetLayerId);
            setDrawingState({ layerId: targetLayerId, locked: false, shape: null });
        },
        handleFetchLayerShapes,
        resetAll: form.reset,
    };
}

async function fetchPaginated<T>(fetcher: (offset: number) => Promise<T[]>, offset: number = 0, accumulated: T[] = []): Promise<T[]> {
    const items = await fetcher(offset);
    const combined = [...accumulated, ...items];
    return items.length === 10 ? fetchPaginated(fetcher, offset + 10, combined) : combined;
}

async function fetchShapesPage(layerId: string, offset: number): Promise<GeoFilter[]> {
    const response = await httpPostGraphql(
        graphql(`
            query GeoLocationLayerShapes($id: UUID!, $offset: Int!) {
                geoLayer(id: $id) {
                    filters(limit: 10, offset: $offset) {
                        id
                        name
                        boundary
                    }
                }
            }
        `),
        { id: layerId, offset },
    );
    return response.geoLayer?.filters ?? [];
}

async function fetchLayersPage(offset: number): Promise<GeoLayer[]> {
    const response = await httpPostGraphql(
        graphql(`
            query GeoLocationsLayers($offset: Int!) {
                geoLayers(limit: 10, offset: $offset) {
                    id
                    name
                }
            }
        `),
        { offset },
    );
    return response.geoLayers ?? [];
}

const queries = {
    layers: queryOptions({
        queryKey: GEO_QUERY_KEY,
        queryFn: () => fetchPaginated(fetchLayersPage),
    }),
};

export const muts = {
    createShape: graphql(`
        mutation CreateGeoFilter($name: String!, $boundary: GeoPolygon!, $layerId: UUID!) {
            createGeoFilter(parameters: { boundary: $boundary, name: $name, layerId: $layerId }) {
                id
            }
        }
    `),
    updateShape: graphql(`
        mutation UpdateGeoFilter($id: UUID!, $name: String!, $boundary: GeoPolygon!, $layerId: UUID) {
            updateGeoFilter(id: $id, parameters: { boundary: $boundary, name: $name, layerId: $layerId }) {
                id
            }
        }
    `),
    archiveShape: graphql(`
        mutation ArchiveGeoFilter($id: UUID!) {
            archiveGeoFilter(id: $id) {
                id
            }
        }
    `),
};
